import { Failure } from '@mrhenry/failure';
import { Dispatch } from 'redux';
import { BookingRequestAction } from '../types/booking-request';
import { UpdateLatch } from '../update-latch/update-latch';

export type Availability = 'AVAILABLE' | 'HAS_OPTION' | 'IS_BOOKED' | 'NOT_AVAILABLE';

export type Week = {
	id: string;
	year: number;
	month: number;
	date: number | undefined;
	day_of_month: number;
	weekNumber: number;
	availability: Availability;
	priceInEuroCents: number | undefined;
	price: string | undefined;
	notThisWeek: boolean | undefined;
}

export type Property = {
	readonly allowNewReservations: boolean,
	readonly thisMonth: Array<Week>,
	readonly nextMonth: Array<Week>
};

export type UpdatePropertyData = {
	readonly type: 'UPDATE_PROPERTY_DATA';
	readonly property: Property | null;
	readonly failure?: Failure;
};

export type PropertyDataUpdateLatch = UpdateLatch<[houseId: number, year: number, month: number], Property>;

export function monthAndYearForPropertyDataRequest( date?: Date|null ): { year: number, month: number } {
	const d = date || new Date();

	return {
		year: d.getUTCFullYear(),
		month: d.getUTCMonth() + 1,
	};
}

export function generatePropertyDataUpdateLatch( dispatch: Dispatch<BookingRequestAction> ): PropertyDataUpdateLatch {
	const latch = new UpdateLatch( async( houseId: number, year: number, month: number ) => {
		return getPropertyData( houseId, year, month );
	}, () => {
		try {
			const property = latch.state();
			dispatch( {
				type: 'UPDATE_PROPERTY_DATA',
				property: property,
			} );
		} catch ( err ) {
			console.log( err );

			dispatch( {
				type: 'UPDATE_PROPERTY_DATA',
				property: null,
				failure: Failure.from( err ),
			} );
		}
	} );

	return latch;
}

export async function getPropertyData( houseId: number, year: number, month: number ): Promise<Property> {
	const thisMonthsYear = year;
	const thisMonth = month;
	let nextMonthsYear = thisMonthsYear;
	let nextMonth = thisMonth + 1;
	if ( 13 === nextMonth ) {
		nextMonth = 1;
		nextMonthsYear += 1;
	}

	const endpoint = new URL( '/graphql', 'https://' + window.Mr.theaddressesApiDomain );
	const response = await fetch( endpoint, {
		credentials: 'include',
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify( {
			query: `query q(
				$houseId: ID!,
				$thisMonthsYear: Int!,
				$thisMonth: Int!,
				$nextMonthsYear: Int!,
				$nextMonth: Int!
			) {
				property(id: $houseId) {
					allowNewReservations
					thisMonth: calendar(year: $thisMonthsYear, month: $thisMonth) {
						...WeekFields
					}
					nextMonth: calendar(year: $nextMonthsYear, month: $nextMonth) {
						...WeekFields
					}
				}
			}

			fragment WeekFields on Week {
				year,
				month,
				date,
				# day_of_month,
				availability,
				priceInEuroCents,
				price
			}`,
			variables: {
				houseId: houseId.toString(),
				thisMonthsYear: thisMonthsYear,
				thisMonth: thisMonth,
				nextMonthsYear: nextMonthsYear,
				nextMonth: nextMonth,
			},
		} ),
	} );

	if ( !response.ok ) {
		throw new Error( `API Error: ${response.status}: ${response.statusText}` );
	}

	const data = await response.json();
	if ( data?.errors?.length ) {
		console.error( 'Error:', data.errors );

		throw new Error( 'API Error' );
	}

	const property = data?.data?.property;

	if ( !( property?.thisMonth && property?.nextMonth ) ) {
		throw new Error( 'invariant : thisMonth and nextMonth are required' );
	}

	return property;
}
